import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CreditCard, Wallet, Banknote, User, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { VowHeader } from "@/components/VowHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { roomNumber, brandName } = useParams();
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [patientName, setPatientName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(`cart_${roomNumber}_${brandName}`);
    if (saved) { try { setCart(JSON.parse(saved)); } catch { setCart([]); } }
    const sn = sessionStorage.getItem("patient_name");
    const sp = sessionStorage.getItem("phone_number");
    if (sn) setPatientName(sn);
    if (sp) setPhoneNumber(sp);
  }, [roomNumber, brandName]);

  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const isKiosk = sessionStorage.getItem("order_source") === "kiosk";

  const handleSubmit = async () => {
    if (!cart.length) { toast.error("Cart is empty"); return; }
    if (!patientName.trim()) { toast.error("Please enter your name"); return; }
    if (!phoneNumber.trim() || phoneNumber.trim().length < 10) { toast.error("Please enter a valid phone number"); return; }

    sessionStorage.setItem("patient_name", patientName.trim());
    sessionStorage.setItem("phone_number", phoneNumber.trim());
    setSubmitting(true);

    try {
      const res = await axios.post(`${API}/orders`, {
        room_number: roomNumber, brand_name: brandName,
        items: cart.map(i => ({ name: i.name, price: i.price, quantity: i.quantity })),
        payment_mode: paymentMode, special_instructions: specialInstructions,
        patient_name: patientName.trim(), phone_number: phoneNumber.trim(),
        source: isKiosk ? "kiosk" : "in_room",
        room_confirmed: !isKiosk,
      });
      localStorage.removeItem(`cart_${roomNumber}_${brandName}`);
      toast.success("Order placed!");
      navigate(`/confirmation/${res.data.order_id}`);
    } catch { toast.error("Failed to place order"); }
    finally { setSubmitting(false); }
  };

  const payments = [
    { value: "cash", label: "Cash", icon: Banknote },
    { value: "upi", label: "UPI", icon: Wallet },
    { value: "card", label: "Card", icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-background pb-28">
      <VowHeader roomNumber={roomNumber} onBack={() => navigate(`/cart/${roomNumber}/${encodeURIComponent(brandName)}`)} />

      <div className="px-4 pt-5 space-y-5">
        {/* Patient Details */}
        <div className="bg-white rounded-xl p-5 border border-[#1c3b2e]/5">
          <h2 className="text-base font-bold text-[#1c3b2e] mb-4">Your Details</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-[#2c2c2c]/60 mb-1.5 block">
                Patient / Attendant Name <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#2c2c2c]/30" />
                <Input value={patientName} onChange={e => setPatientName(e.target.value)}
                  placeholder="Enter name" className="pl-10 h-12 rounded-xl bg-[#f5f1e6]/50 border-[#1c3b2e]/10 focus-visible:ring-[#1c3b2e]"
                  data-testid="patient-name-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#2c2c2c]/60 mb-1.5 block">
                Phone Number <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#2c2c2c]/30" />
                <Input type="tel" value={phoneNumber}
                  onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit number" className="pl-10 h-12 rounded-xl bg-[#f5f1e6]/50 border-[#1c3b2e]/10 focus-visible:ring-[#1c3b2e]"
                  data-testid="phone-number-input" />
              </div>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-xl p-5 border border-[#1c3b2e]/5">
          <h2 className="text-base font-bold text-[#1c3b2e] mb-3">Order Summary</h2>
          <div className="space-y-2">
            {cart.map(item => (
              <div key={item.name} className="flex justify-between text-sm" data-testid={`summary-${item.name}`}>
                <span className="text-[#2c2c2c]/70">{item.name} × {item.quantity}</span>
                <span className="font-semibold text-[#2c2c2c]">₹{(item.price * item.quantity).toFixed(0)}</span>
              </div>
            ))}
            <div className="border-t border-[#1c3b2e]/10 pt-3 mt-2 flex justify-between">
              <span className="font-bold text-[#2c2c2c]">Total</span>
              <span className="font-bold text-[#1c3b2e] text-lg" data-testid="checkout-total">₹{total.toFixed(0)}</span>
            </div>
          </div>
        </div>

        {/* Delivery Estimate */}
        <div className="bg-[#1c3b2e]/5 rounded-xl px-5 py-3.5 flex items-center justify-between">
          <span className="text-sm text-[#2c2c2c]/60">Estimated Delivery</span>
          <span className="text-sm font-bold text-[#1c3b2e]" data-testid="checkout-delivery-time">45-60 min</span>
        </div>

        {/* Payment */}
        <div className="bg-white rounded-xl p-5 border border-[#1c3b2e]/5">
          <h2 className="text-base font-bold text-[#1c3b2e] mb-1">Payment Mode</h2>
          <p className="text-[11px] text-[#2c2c2c]/40 mb-4">Collected at delivery</p>
          <div className="space-y-2.5" data-testid="payment-mode-group">
            {payments.map(opt => {
              const Icon = opt.icon;
              const active = paymentMode === opt.value;
              return (
                <button key={opt.value} onClick={() => setPaymentMode(opt.value)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all min-h-[52px] ${
                    active ? "border-[#1c3b2e] bg-[#1c3b2e]/5" : "border-[#1c3b2e]/10 hover:border-[#1c3b2e]/25"
                  }`}
                  data-testid={`payment-${opt.value}`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? "border-[#1c3b2e]" : "border-[#2c2c2c]/20"}`}>
                    {active && <div className="w-2.5 h-2.5 rounded-full bg-[#1c3b2e]"></div>}
                  </div>
                  <Icon className={`h-5 w-5 ${active ? "text-[#1c3b2e]" : "text-[#2c2c2c]/30"}`} />
                  <span className={`font-medium text-sm ${active ? "text-[#1c3b2e]" : "text-[#2c2c2c]/60"}`}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Special Instructions */}
        <div className="bg-white rounded-xl p-5 border border-[#1c3b2e]/5">
          <h2 className="text-base font-bold text-[#1c3b2e] mb-3">Special Instructions</h2>
          <Textarea placeholder="Any dietary preferences or requests..."
            value={specialInstructions} onChange={e => setSpecialInstructions(e.target.value)}
            className="min-h-20 rounded-xl bg-[#f5f1e6]/50 border-[#1c3b2e]/10 text-sm focus-visible:ring-[#1c3b2e]"
            data-testid="special-instructions" />
        </div>
      </div>

      {/* Kiosk room confirm */}
      {isKiosk && (
        <div className="px-4 pb-2">
          <div className="bg-[#bd9b5e]/10 rounded-xl px-4 py-3 border border-[#bd9b5e]/20" data-testid="kiosk-room-confirm">
            <p className="text-sm text-[#2c2c2c]">
              You're ordering for <span className="font-bold">Room {roomNumber}</span> — is this correct?
            </p>
            <button onClick={() => navigate("/kiosk")}
              className="text-xs text-[#bd9b5e] font-semibold mt-1 underline" data-testid="kiosk-change-room">
              Change room number
            </button>
          </div>
        </div>
      )}

      {/* Submit */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#1c3b2e]/10 p-4">
        <div className="max-w-md mx-auto">
          <button onClick={handleSubmit} disabled={submitting || !cart.length}
            className="w-full bg-[#1c3b2e] text-white rounded-xl h-14 font-semibold text-base hover:bg-[#1c3b2e]/90 disabled:opacity-40 transition-colors min-h-[56px]"
            data-testid="place-order-button">
            {submitting ? "Placing Order..." : "Place Order"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
